import { defineAsyncComponent, type Component } from 'vue'

export type IntegrationSection = 'workspace' | 'personal'

export interface IntegrationPlugin {
  id: string
  section: IntegrationSection
  name: string
  short: string       // 2-3 char fallback when favicon fails
  description: string
  domain: string      // used by googleFaviconUrl()
  modal: Component    // defineAsyncComponent — loaded on first open
}

export const integrationRegistry: IntegrationPlugin[] = [
  {
    id: 'salesforce',
    section: 'workspace',
    name: 'Salesforce',
    short: 'SF',
    description: 'Sync CRM data and unlock Salesforce-backed context.',
    domain: 'salesforce.com',
    modal: defineAsyncComponent(() => import('../components/integrations/SalesforceIntegrationModal.vue')),
  },
  {
    id: 'openai',
    section: 'workspace',
    name: 'OpenAI',
    short: 'OA',
    description: 'Power per-tenant LLM calls and agent execution via OpenAI.',
    domain: 'openai.com',
    modal: defineAsyncComponent(() => import('../components/integrations/OpenAiIntegrationModal.vue')),
  },
  {
    id: 'anthropic',
    section: 'workspace',
    name: 'Claude',
    short: 'CL',
    description: 'Power per-tenant LLM calls and agent execution via Claude.',
    domain: 'claude.ai',
    modal: defineAsyncComponent(() => import('../components/integrations/AnthropicIntegrationModal.vue')),
  },
  {
    id: 'exa',
    section: 'workspace',
    name: 'Exa.ai',
    short: 'EX',
    description: 'Fast, powerful web search for agents.',
    domain: 'exa.ai',
    modal: defineAsyncComponent(() => import('../components/integrations/ExaIntegrationModal.vue')),
  },
  {
    id: 'gong',
    section: 'workspace',
    name: 'Gong',
    short: 'G',
    description: 'Pull call insights and conversation context from Gong.',
    domain: 'gong.io',
    modal: defineAsyncComponent(() => import('../components/integrations/GongIntegrationModal.vue')),
  },
  {
    id: 'warmly',
    section: 'workspace',
    name: 'Warmly',
    short: 'W',
    description: 'Ingest Warmly signals via webhooks and emit them into your workspace.',
    domain: 'warmly.ai',
    modal: defineAsyncComponent(() => import('../components/integrations/WarmlyIntegrationModal.vue')),
  },
  {
    id: 'slack',
    section: 'workspace',
    name: 'Slack',
    short: 'SL',
    description: 'Send notifications to Slack channels when emails are sent.',
    domain: 'slack.com',
    modal: defineAsyncComponent(() => import('../components/integrations/SlackIntegrationModal.vue')),
  },
  {
    id: 'google-workspace',
    section: 'personal',
    name: 'Gmail',
    short: 'GM',
    description: 'Send reachout emails and read responses via Gmail.',
    domain: 'google.com',
    modal: defineAsyncComponent(() => import('../components/integrations/GoogleWorkspaceIntegrationModal.vue')),
  },
  {
    id: 'outreach',
    section: 'personal',
    name: 'Outreach',
    short: 'OR',
    description: 'Sync prospect and sequence data from Outreach.',
    domain: 'outreach.io',
    modal: defineAsyncComponent(() => import('../components/integrations/OutreachIntegrationModal.vue')),
  },
  {
    id: 'pipedrive',
    section: 'personal',
    name: 'Pipedrive',
    short: 'PD',
    description: 'Sync organizations, filters, deals, and mail from Pipedrive.',
    domain: 'pipedrive.com',
    modal: defineAsyncComponent(() => import('../components/integrations/PipedriveIntegrationModal.vue')),
  },
  {
    id: 'hubspot',
    section: 'workspace',
    name: 'HubSpot',
    short: 'HS',
    description: 'Sync companies, contacts, deals, engagements, and emails from HubSpot. Workspace-level — first user to connect creates the connection for the entire team.',
    domain: 'hubspot.com',
    modal: defineAsyncComponent(() => import('../components/integrations/HubspotIntegrationModal.vue')),
  },
]
